import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, DownloadSimple, Folder } from "@phosphor-icons/react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";

const pct = (n, d, digits = 1) => (d ? ((n / d) * 100).toFixed(digits) : "0.0");
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "—");

const Kpi = ({ label, value, sub, testid }) => (
  <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid={testid}>
    <div className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider">{label}</div>
    <div className="text-xl font-semibold text-zinc-900 mt-1">{value}</div>
    {sub && <div className="text-[11px] text-zinc-500 mt-0.5">{sub}</div>}
  </div>
);

const Card = ({ title, sub, children, testid }) => (
  <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid={testid}>
    <h3 className="font-heading text-base font-medium">{title}</h3>
    {sub && <p className="text-xs text-zinc-500 mt-0.5 mb-3">{sub}</p>}
    {!sub && <div className="mb-3" />}
    {children}
  </div>
);

export default function FolderReportPage() {
  const { gid } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    api.folderReport(gid).then(setData).catch((e) => setErr(e?.response?.data?.detail || "Could not load the report"));
  }, [gid]);

  const trend = useMemo(() => (data?.waves || []).filter(w => (w.stats?.sent || 0) > 0).map((w, i) => {
    const s = w.stats || {};
    const m = w.name.match(/(\d+)\s*$/);
    return {
      name: m ? `W${m[1]}` : `#${i + 1}`,
      full: w.name,
      "Open %": +pct(s.unique_opens || 0, s.sent),
      "Click %": +pct(s.unique_clicks || 0, s.sent),
      "Bounce %": +pct(s.bounced || 0, s.sent),
    };
  }), [data]);

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const blob = await api.folderReportPdf(gid);
      const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(data?.group?.name || "folder").replace(/[^a-z0-9]+/gi, "-")}-report.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not generate the PDF");
    } finally {
      setPdfBusy(false);
    }
  };

  if (err) return <div className="p-8 text-sm text-rose-600" data-testid="folder-report-error">{err}</div>;
  if (!data) return <div className="p-8 text-sm text-zinc-400" data-testid="folder-report-loading">Building the report…</div>;

  const k = data.kpis;
  const t = data.trust;
  const funnel = [
    { label: "Reached", n: k.reached },
    { label: "Delivered", n: k.delivered },
    { label: "Opened", n: k.openers },
    { label: "Clicked", n: k.clickers },
    { label: "Unsubscribed", n: k.unsubscribed },
  ];
  const discO = (t.opens?.raw || 0) - (t.opens?.human || 0);
  const discC = (t.clicks?.raw || 0) - (t.clicks?.human || 0);
  const reasonLabels = { "too-fast": "Opened <30s after delivery", "scanner-ua": "Scanner user agent", "bounced-mailbox": "Bounced mailbox", "click-burst": "Rapid repeat clicks", "honeypot": "Honeypot trap", "apple-privacy": "Apple privacy prefetch", "datacenter-ip": "Data-centre IP", "scanner-ip": "Security-vendor IP" };

  return (
    <div className="space-y-4" data-testid="folder-report-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Link to="/campaigns" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900" data-testid="folder-report-back">
            <ArrowLeft size={12}/> Campaigns
          </Link>
          <h2 className="font-heading text-2xl font-medium tracking-tight flex items-center gap-2 mt-1">
            <Folder size={20} className="text-sky-600" weight="fill"/> {data.group.name}
          </h2>
          <p className="text-sm text-zinc-500 mt-0.5">
            Folder report · {data.waves.length} wave{data.waves.length === 1 ? "" : "s"}
            {k.first_sent && <> · {fmtDate(k.first_sent)} → {fmtDate(k.last_sent)}</>}
          </p>
        </div>
        <button data-testid="folder-report-pdf-btn" onClick={downloadPdf} disabled={pdfBusy}
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-50">
          <DownloadSimple size={14}/> {pdfBusy ? "Preparing…" : "Download PDF"}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi testid="fr-kpi-reached" label="People reached" value={k.reached.toLocaleString()}
             sub={`of ${k.mailable_total.toLocaleString()} mailable (${pct(k.reached, k.mailable_total)}%)`}/>
        <Kpi testid="fr-kpi-sent" label="Emails sent" value={k.total_sent.toLocaleString()} sub={`across ${data.waves.length} waves`}/>
        <Kpi testid="fr-kpi-open" label="Open rate (people)" value={`${pct(k.openers, k.reached)}%`} sub={`${k.openers.toLocaleString()} humans opened`}/>
        <Kpi testid="fr-kpi-click" label="Click rate (people)" value={`${pct(k.clickers, k.reached)}%`}
             sub={`${k.clickers.toLocaleString()} clicked · CTOR ${pct(k.clickers, k.openers)}%`}/>
        <Kpi testid="fr-kpi-delivered" label="Delivered" value={`${pct(k.delivered, k.reached)}%`} sub={`${k.delivered.toLocaleString()} webhook-confirmed`}/>
        <Kpi testid="fr-kpi-bounce" label="Hard bounces" value={k.hard_bounces.toLocaleString()}
             sub={`${pct(k.hard_bounces, k.reached)}% · +${k.soft_bounces.toLocaleString()} soft`}/>
        <Kpi testid="fr-kpi-unsub" label="Unsubscribed" value={k.unsubscribed.toLocaleString()} sub={`${pct(k.unsubscribed, k.reached, 2)}% of reached`}/>
        <Kpi testid="fr-kpi-discarded" label="Bot activity discarded" value={(discO + discC).toLocaleString()}
             sub={`${discO.toLocaleString()} opens · ${discC.toLocaleString()} clicks · ${t.hp_hits} honeypot`}/>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card title="Funnel" sub="Unique people at each stage — every contact receives exactly one wave per folder." testid="fr-funnel">
          <div className="space-y-2">
            {funnel.map((f, i) => (
              <div key={f.label} className="flex items-center gap-3" data-testid={`fr-funnel-${f.label.toLowerCase()}`}>
                <div className="w-24 text-xs text-zinc-600">{f.label}</div>
                <div className="flex-1 h-6 bg-zinc-100 rounded overflow-hidden">
                  <div className={`h-full ${i === 4 ? "bg-zinc-400" : "bg-sky-600"}`} style={{ width: `${Math.max(k.reached ? (f.n / k.reached) * 100 : 0, f.n > 0 ? 1.5 : 0)}%` }}/>
                </div>
                <div className="w-28 text-right text-xs">
                  <span className="font-medium">{f.n.toLocaleString()}</span>
                  <span className="text-zinc-400"> · {pct(f.n, k.reached)}%</span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Wave-by-wave trend" sub="Human open/click/bounce rates per wave — watch whether later batches hold up." testid="fr-trend">
          {trend.length >= 2 ? (
            <ResponsiveContainer width="100%" height={210}>
              <LineChart data={trend} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5"/>
                <XAxis dataKey="name" tick={{ fontSize: 11 }}/>
                <YAxis tick={{ fontSize: 11 }} unit="%"/>
                <Tooltip labelFormatter={(l, p) => p?.[0]?.payload?.full || l}/>
                <Legend wrapperStyle={{ fontSize: 11 }}/>
                <Line type="monotone" dataKey="Open %" stroke="#0284c7" strokeWidth={2} dot={{ r: 2.5 }}/>
                <Line type="monotone" dataKey="Click %" stroke="#059669" strokeWidth={2} dot={{ r: 2.5 }}/>
                <Line type="monotone" dataKey="Bounce %" stroke="#e11d48" strokeWidth={2} dot={{ r: 2.5 }}/>
              </LineChart>
            </ResponsiveContainer>
          ) : <div className="text-xs text-zinc-400 py-8 text-center">Needs at least two sent waves to draw a trend.</div>}
        </Card>
      </div>

      <Card title="Waves" testid="fr-waves">
        <table className="w-full text-sm">
          <thead className="text-xs text-zinc-500"><tr>
            <th className="text-left py-1.5 font-medium">Wave</th>
            <th className="text-left py-1.5 font-medium">Sent</th>
            <th className="text-right py-1.5 font-medium">Recipients</th>
            <th className="text-right py-1.5 font-medium">Delivered to</th>
            <th className="text-right py-1.5 font-medium">Open rate</th>
            <th className="text-right py-1.5 font-medium">Click rate</th>
            <th className="text-right py-1.5 font-medium">Bounced</th>
            <th className="text-right py-1.5 font-medium">Unsubs</th>
          </tr></thead>
          <tbody>
            {data.waves.map(w => {
              const s = w.stats || {};
              return (
                <tr key={w.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`fr-wave-${w.id}`}>
                  <td className="py-1.5 pr-2"><Link to={`/campaigns/${w.id}`} className="text-sky-700 hover:underline">{w.name}</Link>
                    {w.status !== "sent" && <span className="ml-2 text-[10px] uppercase text-zinc-400">{w.status}</span>}</td>
                  <td className="py-1.5 text-zinc-500 text-xs">{fmtDate(w.sent_at)}</td>
                  <td className="py-1.5 text-right">{(s.recipients || 0).toLocaleString()}</td>
                  <td className="py-1.5 text-right">{(s.sent || 0).toLocaleString()}</td>
                  <td className="py-1.5 text-right font-medium">{pct(s.unique_opens || 0, s.sent)}%</td>
                  <td className="py-1.5 text-right font-medium">{pct(s.unique_clicks || 0, s.sent)}%</td>
                  <td className={`py-1.5 text-right ${s.bounced ? "text-rose-600" : "text-zinc-400"}`}>{(s.bounced || 0).toLocaleString()}</td>
                  <td className="py-1.5 text-right text-zinc-500">{(s.unsubscribed || 0).toLocaleString()}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Card title="Most clicked links" sub="Human clicks only, UTM tags stripped." testid="fr-links">
          {data.top_links.length === 0 ? <div className="text-xs text-zinc-400">No human clicks yet.</div> : (
            <div className="space-y-2">
              {data.top_links.map(l => (
                <div key={l.url} className="flex items-center gap-3 text-sm">
                  <div className="flex-1 truncate text-zinc-700" title={l.url}>{l.url.replace(/^https?:\/\//, "")}</div>
                  <div className="text-xs whitespace-nowrap"><span className="font-medium">{l.unique_clickers}</span> <span className="text-zinc-400">people · {l.clicks} clicks</span></div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title="Most engaged companies" sub="Unique human openers and clickers per company." testid="fr-companies">
          {data.top_companies.length === 0 ? <div className="text-xs text-zinc-400">No engagement yet.</div> : (
            <table className="w-full text-sm">
              <thead className="text-xs text-zinc-500"><tr>
                <th className="text-left py-1 font-medium">Company</th>
                <th className="text-right py-1 font-medium">Openers</th>
                <th className="text-right py-1 font-medium">Clickers</th>
              </tr></thead>
              <tbody>
                {data.top_companies.map(c => (
                  <tr key={c.company} className="border-t border-zinc-100">
                    <td className="py-1.5 truncate max-w-[220px]" title={c.company}>{c.company}</td>
                    <td className="py-1.5 text-right">{c.openers}</td>
                    <td className="py-1.5 text-right font-medium">{c.clickers}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      <Card title="Most engaged people" sub="Ranked by human clicks, then opens, across all waves." testid="fr-people">
        {data.top_people.length === 0 ? <div className="text-xs text-zinc-400">No engagement yet.</div> : (
          <table className="w-full text-sm">
            <thead className="text-xs text-zinc-500"><tr>
              <th className="text-left py-1 font-medium">Person</th>
              <th className="text-left py-1 font-medium">Company</th>
              <th className="text-right py-1 font-medium">Opens</th>
              <th className="text-right py-1 font-medium">Clicks</th>
              <th className="text-right py-1 font-medium">Last activity</th>
            </tr></thead>
            <tbody>
              {data.top_people.map(p => (
                <tr key={p.contact_id} className="border-t border-zinc-100 hover:bg-zinc-50">
                  <td className="py-1.5">
                    <Link to={`/contacts/${p.contact_id}`} className="text-sky-700 hover:underline">{p.name}</Link>
                    <div className="text-[11px] text-zinc-400">{p.email}{p.job_title ? ` · ${p.job_title}` : ""}</div>
                  </td>
                  <td className="py-1.5 text-zinc-600 truncate max-w-[200px]">{p.company || "—"}</td>
                  <td className="py-1.5 text-right">{p.opens}</td>
                  <td className="py-1.5 text-right font-medium">{p.clicks}</td>
                  <td className="py-1.5 text-right text-xs text-zinc-500">{fmtDate(p.last)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Stats you can defend" sub="Raw = everything tracking recorded. Human = after discarding scanner and bot activity. All figures above use human numbers." testid="fr-trust">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div><div className="text-[11px] text-zinc-500 uppercase tracking-wider">Raw opens</div><div className="font-medium">{(t.opens?.raw || 0).toLocaleString()}</div></div>
          <div><div className="text-[11px] text-zinc-500 uppercase tracking-wider">Human opens</div><div className="font-medium">{(t.opens?.human || 0).toLocaleString()}</div></div>
          <div><div className="text-[11px] text-zinc-500 uppercase tracking-wider">Raw clicks</div><div className="font-medium">{(t.clicks?.raw || 0).toLocaleString()}</div></div>
          <div><div className="text-[11px] text-zinc-500 uppercase tracking-wider">Human clicks</div><div className="font-medium">{(t.clicks?.human || 0).toLocaleString()}</div></div>
        </div>
        {Object.keys(t.reasons || {}).length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {Object.entries(t.reasons).sort((a, b) => b[1] - a[1]).map(([r, n]) => (
              <span key={r} className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800">
                {reasonLabels[r] || r}: {n.toLocaleString()}
              </span>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
